import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Chip,
  MenuItem,
} from "@material-ui/core";
import { useFormik, FormikProvider } from "formik";
import { toast } from "react-toastify";
import { ApiResponse } from "apisauce";

import { HydrovacTicket } from "../../interface/api/HydrovacTicket";
import { bPrefix } from "../../utils/parser";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import { Load } from "../../interface/api/soilflo/Load";
import hydrovacTicketService from "../../services/hydrovacTicketService";
import ticketService from "../../services/ticketService";
import contractorCompanyService from "../../services/contractorCompanyService";
import storageAreaService from "../../services/storageAreaService";
import { ContainmentPad } from "../../interface/api/ContainmentPad";
import FormSelectSearchField from "../shared/form-select-search-field";
import FormDateTimePicker from "../shared/form-date-time-picker";
import { SoilFLOIcon } from "../shared/icons";
import PageHero from "../shared/page-hero";
import LoadingView from "../shared/loading-view";
import { useTicketCrud } from "../../hooks/useTicketCrud";
import { history } from "../..";
import { useAppSelector } from "../../hooks";
import { getCurrentUser } from "../../store/userStore";
import { HydrovacScreeningQuestions, HydrovacDistanceOptions, HydrovacYesNoOptions } from "../../utils/constants";
import { useSoilFLOStyles } from "../../utils/soilflo-styles";
import { removeSeconds } from "../../utils/date-helper";
import PriceField from "../shared/price-field";
import FormTruckLicensePlateField from "../shared/form-truck-license-plate-field";
import transportCompanyService from "../../services/transportCompanyService";

const HydrovacTicketView = () => {
  const classes = useSoilFLOStyles();
  const { id } = useParams<{ id: string }>();
  const user = useAppSelector(getCurrentUser);
  const { ticket, loading, loadTicket } =
    useTicketCrud<HydrovacTicket>(id, hydrovacTicketService);
  const [contractors, setContractors] = useState<{ value: number; label: string }[]>([]);
  const [transportCompanies, setTransportCompanies] = useState<{ value: number; label: string }[]>([]);
  const [soilFLOLoad, setSoilFLOLoad] = useState<Load | null>(null);
  const [padName, setPadName] = useState<string>("");

  const formik = useFormik<HydrovacTicket>({
    initialValues: (ticket ?? {}) as HydrovacTicket,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok && response.data) {
        const data = response.data as ContractorCompaniesWithThresholdsResponse;
        const companies = data.contractorCompanies.filter((c) => c.isHydrovac);
        setContractors(companies.map((c) => ({ value: c.id, label: c.name })));
      }
    });
    transportCompanyService.getTransportCompanies().then((response) => {
      if (response.ok && response.data) {
        const companies = response.data as any[];
        setTransportCompanies(companies.map((c) => ({ value: c.id, label: c.name })));
      }
    });
  }, []);

  // Resolve the assigned containment pad name (read-only; chosen at receive).
  useEffect(() => {
    const padId = formik.values.containmentPadId;
    if (padId) {
      storageAreaService.getContainmentPad(padId).then((r) => {
        if (r.ok && r.data) setPadName((r.data as ContainmentPad).name ?? "");
      });
    } else {
      setPadName("");
    }
  }, [formik.values.containmentPadId]);

  // Fetch SoilFLO data and map into ticket fields
  useEffect(() => {
    if (ticket?.isSoilFLOLoad) {
      ticketService.getLoad(`${bPrefix(ticket)}${ticket.ticketNumber}`).then((response) => {
        if (response.ok) {
          const data = response.data as Load[];
          if (data && data.length > 0) {
            const load = data.sort((a, b) => b.id - a.id)[0];
            setSoilFLOLoad(load);
            formik.setValues((prev) => ({
              ...prev,
              propertyAddress: load.sourceSite?.name ?? prev.propertyAddress,
              contractorCompanyName:
                load.sourceSite?.contractingcompany?.name ??
                prev.contractorCompanyName,
              shippingCompany:
                load.truck?.global_truck?.hauler?.name ?? prev.shippingCompany,
              shippingDriver:
                load.truck?.driverName ?? prev.shippingDriver,
              truckNumber:
                load.truck?.global_truck?.number ?? prev.truckNumber,
              truckLicensePlate:
                load.truck?.global_truck?.license ?? prev.truckLicensePlate,
              timeLoaded: load.dispatchedTime ?? prev.timeLoaded,
            }));
          }
        }
      });
    }
  }, [ticket?.id]);

  const isSoilFLO = ticket?.isSoilFLOLoad && soilFLOLoad != null;

  const onEnterHeadspace = async () => {
    if (!ticket) return;
    const ticketToSubmit = { ...formik.values };
    const response: ApiResponse<any> =
      await hydrovacTicketService.clearHeadspace(ticket.id, ticketToSubmit);
    if (response.ok) {
      toast.success("Headspace entered");
      loadTicket();
    } else {
      toast.error("Failed to enter headspace");
    }
  };

  const onClearHeadspace = async () => {
    if (!ticket) return;
    const blank = {
      ...formik.values,
      headspaceReading: null,
      visualInspectionResult: null,
      inspect1_OrganicMaterial: null,
      inspect2_Insects: null,
      inspect3_UnusualOdours: null,
      inspect4_OtherMaterial: null,
      inspect5_HydrocarbonSheen: null,
      inspect6_HeadspaceAbove200: null,
    } as HydrovacTicket;
    const response: ApiResponse<any> =
      await hydrovacTicketService.clearHeadspace(ticket.id, blank);
    if (response.ok) {
      toast.success("Headspace cleared — ticket set to pending");
      loadTicket();
    } else {
      toast.error("Failed to clear headspace");
    }
  };

  const onSaveTicket = async () => {
    if (!formik.values) return;
    const _ticket = { ...formik.values };
    _ticket.timeLoaded = removeSeconds(_ticket.timeLoaded);
    _ticket.timeUnloaded = removeSeconds(_ticket.timeUnloaded);
    const response = await hydrovacTicketService.saveTicket(_ticket);
    if (response.ok) {
      toast.success("Ticket saved");
      loadTicket();
    } else {
      toast.error("Failed to save ticket");
    }
  };

  const onUnreceive = async () => {
    if (!ticket) return;
    const response = await hydrovacTicketService.unreceiveTicket(ticket.id);
    if (response.ok) {
      toast.success("Ticket unreceived");
      loadTicket();
    } else {
      toast.error("Failed to unreceive ticket");
    }
  };

  const inspectionChecklist = [
    { field: "inspect1_OrganicMaterial", label: "Organic Material" },
    { field: "inspect2_Insects", label: "Insects" },
    { field: "inspect3_UnusualOdours", label: "Unusual Odours" },
    { field: "inspect4_OtherMaterial", label: "Other Material" },
    { field: "inspect5_HydrocarbonSheen", label: "Hydrocarbon Sheen" },
    { field: "inspect6_HeadspaceAbove200", label: "Headspace Above 200" },
  ];

  return (
    <LoadingView loading={loading} data={ticket} notFoundMessage="Ticket not found">
      {ticket && (
        <FormikProvider value={formik}>
          <Container maxWidth="md">
            <PageHero
              title=""
              subtitle=""
              showBackButton={true}
            />
            {ticket.isReceived && (
              <Box display="flex" alignItems="center" mt={2} mb={1}>
                <Chip label="Received" color="primary" style={{ marginRight: 12 }} />
                {ticket.isPendingHeadspace && (
                  <Chip label="Pending Headspace" color="secondary" style={{ marginRight: 12 }} />
                )}
                {ticket.isComplete && (
                  <Chip
                    label="Complete"
                    style={{ backgroundColor: "#4caf50", color: "#fff", marginRight: 12 }}
                  />
                )}
                <Button variant="outlined" color="secondary" size="small" onClick={onUnreceive}>
                  Unreceive
                </Button>
              </Box>
            )}

            {isSoilFLO && (
              <Box display="flex" alignItems="center" mt={2} mb={1} style={{ gap: 8 }}>
                <SoilFLOIcon />
                <Typography variant="body2" color="textSecondary">
                  Fields pulled from SoilFLO are read-only
                </Typography>
              </Box>
            )}

            {/* Section 1: Source Info */}
            <Paper style={{ padding: 24, marginTop: 16 }}>
              <Typography variant="h6" gutterBottom>
                Section 1: Source Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Contractor"
                      value={formik.values.contractorCompanyName ?? ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormSelectSearchField
                      name="contractorCompanyId"
                      placeholder="Contractor"
                      options={contractors}
                      value={
                        formik.values.contractorCompanyId
                          ? contractors.find((c) => c.value === formik.values.contractorCompanyId) ?? null
                          : null
                      }
                      onChange={(option: any) => {
                        if (option) {
                          formik.setValues({
                            ...formik.values,
                            contractorCompanyId: option.value,
                            contractorCompanyName: option.label,
                          });
                        } else {
                          formik.setValues({
                            ...formik.values,
                            contractorCompanyId: null,
                            contractorCompanyName: null,
                          });
                        }
                      }}
                      isClearable
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Property Address"
                    value={formik.values.propertyAddress ?? ""}
                    onChange={(e) =>
                      !isSoilFLO && formik.setFieldValue("propertyAddress", e.target.value)
                    }
                    InputProps={{ readOnly: isSoilFLO }}
                    InputLabelProps={isSoilFLO ? { shrink: true } : undefined}
                    className={isSoilFLO ? classes.soilFLOField : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Land Use"
                    value={formik.values.landUse ?? ""}
                    onChange={(e) => formik.setFieldValue("landUse", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Description of Activity"
                    value={formik.values.descriptionOfActivity ?? ""}
                    onChange={(e) =>
                      formik.setFieldValue("descriptionOfActivity", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Work Order Number"
                    value={formik.values.workOrderNumber ?? ""}
                    onChange={(e) => formik.setFieldValue("workOrderNumber", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Property Owner"
                    value={formik.values.propertyOwner ?? ""}
                    onChange={(e) => formik.setFieldValue("propertyOwner", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Shipping Company"
                      value={formik.values.shippingCompany ?? ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormSelectSearchField
                      name="transportCompanyId"
                      placeholder="Shipping Company"
                      options={transportCompanies}
                      value={
                        formik.values.transportCompanyId
                          ? transportCompanies.find(
                              (c) => c.value === formik.values.transportCompanyId
                            ) ?? (formik.values.shippingCompany
                              ? { value: formik.values.transportCompanyId, label: formik.values.shippingCompany }
                              : null)
                          : null
                      }
                      onChange={(option: any) => {
                        if (option) {
                          formik.setValues({
                            ...formik.values,
                            transportCompanyId: option.value,
                            shippingCompany: option.label,
                          });
                        } else {
                          formik.setValues({
                            ...formik.values,
                            transportCompanyId: null,
                            shippingCompany: null,
                          });
                        }
                      }}
                      isClearable
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Shipping Driver"
                    value={formik.values.shippingDriver ?? ""}
                    onChange={(e) => formik.setFieldValue("shippingDriver", e.target.value)}
                    InputProps={{ readOnly: isSoilFLO }}
                    className={isSoilFLO ? classes.soilFLOField : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Truck Number"
                    value={formik.values.truckNumber ?? ""}
                    onChange={(e) => !isSoilFLO && formik.setFieldValue("truckNumber", e.target.value)}
                    InputProps={{ readOnly: isSoilFLO }}
                    className={isSoilFLO ? classes.soilFLOField : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Truck License Plate"
                      value={formik.values.truckLicensePlate?.toUpperCase() ?? ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormTruckLicensePlateField
                      value={formik.values.truckLicensePlate ?? ""}
                      name="truckLicensePlate"
                      label="Truck License Plate"
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={isSoilFLO ? classes.soilFLOField : undefined}>
                    <FormDateTimePicker
                      label="Time Loaded"
                      value={formik.values.timeLoaded ? new Date(formik.values.timeLoaded) : null}
                      onChange={(date: any) =>
                        formik.setFieldValue("timeLoaded", date)
                      }
                      readOnly={isSoilFLO}
                      clearable={!isSoilFLO}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormDateTimePicker
                    label="Time Unloaded"
                    value={formik.values.timeUnloaded ? new Date(formik.values.timeUnloaded) : null}
                    onChange={(date: any) =>
                      formik.setFieldValue("timeUnloaded", date)
                    }
                    clearable
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PriceField
                    label="Sale Price"
                    value={formik.values.salePrice}
                    onChange={(val) => formik.setFieldValue("salePrice", val)}
                  />
                </Grid>
                {formik.values.driverSignature && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                      Driver Signature
                    </Typography>
                    <img
                      src={formik.values.driverSignature}
                      alt="Driver Signature"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 150,
                        display: "block",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Section 2: Screening Questions */}
            <Paper style={{ padding: 24, marginTop: 16 }}>
              <Typography variant="h6" gutterBottom>
                Section 2: Screening Questions
              </Typography>
              <Grid container spacing={3}>
                {HydrovacScreeningQuestions.map(({ field, label, type }) => {
                  const options = type === "distance" ? HydrovacDistanceOptions : HydrovacYesNoOptions;
                  return (
                    <Grid item xs={12} sm={6} key={field}>
                      <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 500 }}>
                        {label}
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        value={(formik.values as any)[field] ?? ""}
                        onChange={(e) =>
                          formik.setFieldValue(field, e.target.value)
                        }
                      >
                        <MenuItem value=""><em>Select...</em></MenuItem>
                        {options.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Section 3: Field Assessment */}
            <Paper style={{ padding: 24, marginTop: 16 }}>
              <Typography variant="h6" gutterBottom>
                Section 3: Field Assessment
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Headspace Reading"
                    value={formik.values.headspaceReading ?? ""}
                    onChange={(e) =>
                      formik.setFieldValue("headspaceReading", e.target.value || null)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Visual Inspection Result"
                    value={formik.values.visualInspectionResult ?? ""}
                    onChange={(e) =>
                      formik.setFieldValue("visualInspectionResult", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Containment Pad"
                    value={padName}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Assigned at receive"
                  />
                  {formik.values.containmentPadId && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() =>
                        history.push(
                          `/containment-pad-sampling/${formik.values.containmentPadId}`
                        )
                      }
                    >
                      Open Containment Pad
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label="Containment Bay"
                    value={formik.values.containmentBay ?? ""}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "containmentBay",
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {[1, 2, 3, 4].map((bay) => (
                      <MenuItem key={bay} value={bay}>
                        {bay}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>

            {/* Section 4: Inspection Checklist */}
            <Paper style={{ padding: 24, marginTop: 16 }}>
              <Typography variant="h6" gutterBottom>
                Section 4: Inspection Checklist
              </Typography>
              <Grid container spacing={2}>
                {inspectionChecklist.map(({ field, label }) => (
                  <Grid item xs={12} sm={4} key={field}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(formik.values as any)[field] ?? false}
                          onChange={(e) =>
                            formik.setFieldValue(field, e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Notes (Section 5 sign-off is now the bulk QP approval on the pad) */}
            <Paper style={{ padding: 24, marginTop: 16 }}>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    label="Notes"
                    value={formik.values.notes ?? ""}
                    onChange={(e) => formik.setFieldValue("notes", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end" flexWrap="wrap" mt={2} mb={4}>
              {ticket.isComplete && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClearHeadspace}
                  style={{ marginRight: 8, marginBottom: 8 }}
                >
                  Clear Headspace
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={onSaveTicket}
                style={{ marginBottom: 8 }}
              >
                Save
              </Button>
            </Box>
          </Container>
        </FormikProvider>
      )}
    </LoadingView>
  );
};

export default HydrovacTicketView;
